<template>
  <div class="lp-page pricing-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ $t('pricing.badge') || 'PRICING' }}</span>
        <h1 class="lp-title">{{ $t('pricing.title') }}</h1>
        <p class="lp-lead">{{ $t('pricing.subtitle') }}</p>
      </div>
    </section>

    <!-- Plans -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <!-- Billing toggle -->
        <div class="billing-toggle reveal-item">
          <span :class="{ active: !isYearly }">{{ $t('pricing.monthly') || 'Monthly' }}</span>
          <label class="toggle-switch">
            <input type="checkbox" v-model="isYearly" />
            <span class="slider"></span>
          </label>
          <span :class="{ active: isYearly }">
            {{ $t('pricing.yearly') || 'Yearly' }}
            <span class="save-badge">{{ $t('pricing.save20') || 'Save 20%' }}</span>
          </span>
        </div>

        <div class="pricing-grid">
          <div
            v-for="(plan, key) in translatedPlans"
            :key="key"
            :class="['price-card', { featured: plan.is_featured }]"
          >
            <div v-if="plan.is_featured" class="featured-badge">{{ $t('pricing.featured') }}</div>

            <h3 class="plan-name">{{ plan.name }}</h3>
            <p class="plan-desc">{{ plan.description }}</p>

            <div class="plan-price">
              <span v-if="!plan.is_free" class="currency">€</span>
              <span class="amount">{{ displayPrice(plan) }}</span>
              <span v-if="!plan.is_free" class="period">/{{ isYearly ? $t('pricing.year') : $t('pricing.month') }}</span>
              <span v-else class="period">{{ $t('pricing.free') }}</span>
            </div>

            <p v-if="!plan.is_free && isYearly" class="yearly-note">
              ${{ plan.amount }}/{{ $t('pricing.month') }} {{ $t('pricing.billedYearly') || 'billed yearly' }}
            </p>

            <button
              :class="['lp-btn', plan.is_featured ? 'lp-btn-primary' : 'lp-btn-secondary', 'plan-button']"
              @click="selectPlan(key)"
            >
              {{ plan.is_free ? $t('pricing.getStarted') : $t('pricing.subscribe') }}
            </button>

            <ul class="plan-features">
              <li v-for="(feature, index) in plan.features" :key="index">
                <font-awesome-icon icon="fa-solid fa-circle-check" />
                <span>{{ feature }}</span>
              </li>
            </ul>

            <div class="plan-info">
              <p v-if="!plan.is_free">{{ isYearly ? $t('pricing.billYearly') : $t('pricing.billMonthly') }}</p>
              <p>{{ $t('pricing.scanLimit', { limit: plan.scan_limit }) }}</p>
              <p v-if="plan.hard_scan_limit > 0">{{ $t('pricing.hardScanLimit', { limit: plan.hard_scan_limit }) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="lp-section">
      <div class="lp-narrow">
        <div class="lp-section-head">
          <h2 class="lp-h2">{{ $t('pricing.faq.title') }}</h2>
        </div>
        <div class="faq-grid">
          <div class="faq-card reveal-item">
            <h4>{{ $t('pricing.faq.question1') }}</h4>
            <p class="lp-text">{{ $t('pricing.faq.answer1') }}</p>
          </div>
          <div class="faq-card reveal-item">
            <h4>{{ $t('pricing.faq.question2') }}</h4>
            <p class="lp-text">{{ $t('pricing.faq.answer2') }}</p>
          </div>
          <div class="faq-card reveal-item">
            <h4>{{ $t('pricing.faq.question3') }}</h4>
            <p class="lp-text">{{ $t('pricing.faq.answer3') }}</p>
          </div>
          <div class="faq-card reveal-item">
            <h4>{{ $t('pricing.faq.question4') }}</h4>
            <p class="lp-text">{{ $t('pricing.faq.answer4') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-cta">
          <h2>{{ $t('pricing.cta.title') }}</h2>
          <p>{{ $t('pricing.cta.subtitle') }}</p>
          <div class="cta-buttons">
            <a href="/auth/google" class="lp-btn lp-btn-primary">{{ $t('pricing.cta.button') }}</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Navbar from '../components/Navbar.vue';
import Footer from '../components/Footer.vue';
import api from '@/utils/api';
import { useReveal } from '../composables/useReveal';

const { t } = useI18n();
const plans = ref({});
const isYearly = ref(false);
const loading = ref(true);

onMounted(async () => {
  window.scrollTo({ top: 0, behavior: 'instant' });
  await loadPlans();
});

useReveal();

const loadPlans = async () => {
  loading.value = true;
  try {
    const response = await api.get('/api/pricing/plans');
    plans.value = response.data.plans || {};
  } catch (error) {
    console.error('Error fetching pricing plans:', error);
  } finally {
    loading.value = false;
  }
};

const displayPrice = (plan) => {
  if (plan.is_free) return 0;
  if (isYearly.value) {
    const monthlyPrice = parseFloat(plan.amount);
    const yearlyTotal = monthlyPrice * 12;
    const discountedYearly = yearlyTotal * 0.8;
    return Math.round(discountedYearly);
  }
  return plan.amount;
};

const translatedPlans = computed(() => {
  const translated = {};
  Object.keys(plans.value).forEach(key => {
    const plan = plans.value[key];
    const planKey = key.toLowerCase().replace(' ', '_');
    translated[key] = {
      ...plan,
      name: t(`pricing.plans.${planKey}.name`, plan.name),
      description: t(`pricing.plans.${planKey}.description`, plan.description),
      features: plan.features.map((feature, index) => {
        return t(`pricing.plans.${planKey}.features.${index}`, feature);
      })
    };
  });
  return translated;
});

const selectPlan = (planKey) => {
  if (plans.value[planKey]?.is_free) {
    window.location.href = '/auth/google';
  } else {
    window.location.href = `/subscribe/${planKey}`;
  }
};
</script>

<style scoped>
/* Billing toggle */
.billing-toggle { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 44px; font-weight: 600; color: var(--muted); font-size: 15px; }
.billing-toggle span.active { color: var(--ink); }
.save-badge { display: inline-block; margin-left: 6px; padding: 3px 9px; border-radius: var(--pill); background: rgba(16,185,129,0.14); color: #0f9b6a; font-size: 11px; font-weight: 700; }
.toggle-switch { position: relative; display: inline-block; width: 52px; height: 28px; }
.toggle-switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; inset: 0; background: #d7dce4; border-radius: var(--pill); cursor: pointer; transition: background 0.2s ease; }
.slider::before { content: ''; position: absolute; height: 22px; width: 22px; left: 3px; top: 3px; background: #fff; border-radius: 50%; transition: transform 0.2s ease; box-shadow: 0 2px 6px rgba(0,0,0,0.2); }
.toggle-switch input:checked + .slider { background: var(--violet); }
.toggle-switch input:checked + .slider::before { transform: translateX(24px); }

/* Pricing cards */
.pricing-grid { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 22px; max-width: 1100px; margin: 0 auto; }
.price-card {
  position: relative; background: var(--card); border: 1px solid var(--line); border-radius: var(--radius);
  padding: 34px 30px; box-shadow: 0 26px 60px -38px rgba(31,41,51,0.32);
  flex: 1 1 300px; max-width: 340px;
  display: flex; flex-direction: column; transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}
.price-card:hover { transform: translateY(-6px); box-shadow: 0 36px 80px -36px rgba(31,41,51,0.4); }
.price-card.featured { border-color: var(--violet); box-shadow: 0 36px 80px -34px rgba(43,130,240,0.45); }
.featured-badge { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: var(--violet); color: #fff; font-size: 12px; font-weight: 700; padding: 6px 16px; border-radius: var(--pill); white-space: nowrap; }
.plan-name { font-size: 21px; font-weight: 700; color: var(--ink); }
.plan-desc { color: var(--muted); font-size: 14px; margin: 6px 0 18px; line-height: 1.5; min-height: 42px; }
.plan-price { display: flex; align-items: baseline; gap: 2px; margin-bottom: 4px; }
.plan-price .currency { font-size: 22px; font-weight: 700; color: var(--ink); }
.plan-price .amount { font-size: 46px; font-weight: 800; letter-spacing: -0.03em; color: var(--ink); line-height: 1; }
.plan-price .period { font-size: 15px; color: var(--muted); font-weight: 500; }
.yearly-note { font-size: 12.5px; color: var(--muted); margin: 0 0 18px; }
.plan-button { width: 100%; margin: 18px 0 22px; }
.plan-features { list-style: none; margin: 0; padding: 22px 0 0; border-top: 1px solid var(--line); display: flex; flex-direction: column; gap: 12px; flex: 1; }
.plan-features li { display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: var(--ink); line-height: 1.45; }
.plan-features li svg { color: var(--violet); margin-top: 2px; flex-shrink: 0; }
.plan-info { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--line); }
.plan-info p { font-size: 12.5px; color: var(--muted); margin: 2px 0; }

/* FAQ */
.faq-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.faq-card { background: var(--card); border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 24px 26px; box-shadow: 0 18px 44px -34px rgba(31,41,51,0.3); }
.faq-card h4 { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0 0 8px; letter-spacing: -0.01em; }

.cta-buttons { display: inline-flex; gap: 14px; flex-wrap: wrap; justify-content: center; }

@media (max-width: 768px) {
  .faq-grid { grid-template-columns: 1fr; }
}
</style>
